import { useCallback, useEffect, useRef, useState } from 'react';
import { docsService } from '@web/services/singletons';
import { DocumentVersion, PublicationGroupStatus, PublicationStatusEnum } from '@web/vin-sessions/types';

export const findPublishedVersion = (versions: DocumentVersion[], documentId?: string): DocumentVersion | undefined =>
    versions.find(
        (version) => version.documentId !== documentId && version.status === PublicationGroupStatus.Published
    );

interface Options {
    documentId?: string;
    status?: string;
    versions: DocumentVersion[];
    setVersions: (update: (versions: DocumentVersion[]) => DocumentVersion[]) => void;
    changeStatus: (status: string) => void;
    onArchiveFailed: (data: unknown) => void;
    missingRequiredFields: string[];
    onMissingRequired: () => void;
}

export const usePublishGuard = ({
    documentId,
    status,
    versions,
    setVersions,
    changeStatus,
    onArchiveFailed,
    missingRequiredFields,
    onMissingRequired,
}: Options) => {
    const [versionToArchive, setVersionToArchive] = useState<DocumentVersion>();
    const [isArchiving, setIsArchiving] = useState(false);
    const missingRequiredRef = useRef<{ documentId?: string; fields: string[] }>();
    const isMissingRequired = missingRequiredFields.length > 0;

    useEffect(() => {
        const previous = missingRequiredRef.current;
        missingRequiredRef.current = { documentId, fields: missingRequiredFields };
        const hasNewlyEmptiedField =
            previous !== undefined &&
            previous.documentId === documentId &&
            !previous.fields.includes('publicationType') &&
            missingRequiredFields.some((field) => !previous.fields.includes(field));
        if (hasNewlyEmptiedField && status === PublicationStatusEnum.Published) {
            changeStatus(PublicationStatusEnum.Draft);
            onMissingRequired();
        }
    }, [changeStatus, documentId, missingRequiredFields, onMissingRequired, status]);

    const requestStatusChange = useCallback(
        (nextStatus: string) => {
            if (nextStatus === PublicationStatusEnum.Published && isMissingRequired) {
                changeStatus(PublicationStatusEnum.Draft);
                onMissingRequired();
                return;
            }
            const publishedVersion =
                nextStatus === PublicationStatusEnum.Published ? findPublishedVersion(versions, documentId) : undefined;
            if (publishedVersion) {
                setVersionToArchive(publishedVersion);
                return;
            }
            changeStatus(nextStatus);
        },
        [changeStatus, documentId, isMissingRequired, onMissingRequired, versions]
    );

    const cancel = useCallback(() => {
        if (!isArchiving) {
            setVersionToArchive(undefined);
        }
    }, [isArchiving]);

    const archiveAndPublish = useCallback(async () => {
        if (!versionToArchive) {
            return;
        }
        setIsArchiving(true);
        const response = await docsService.deletePublicationById(versionToArchive.documentId);
        setIsArchiving(false);
        setVersionToArchive(undefined);
        if (!response.success) {
            if (!response.aborted) {
                onArchiveFailed(response.data);
            }
            return;
        }
        setVersions((current) =>
            current.map((version) =>
                version.documentId === versionToArchive.documentId
                    ? { ...version, status: PublicationGroupStatus.Archived }
                    : version
            )
        );
        changeStatus(PublicationStatusEnum.Published);
    }, [changeStatus, onArchiveFailed, setVersions, versionToArchive]);

    return { versionToArchive, isArchiving, requestStatusChange, cancel, archiveAndPublish };
};
